import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';

import { setFailedValidations } from 'app/App/actions';
import { EDIT_STATUS_NEW } from 'app/App/const';
import editStatusSelector from 'app/App/selectors/editStatus';
import failedValidationsSelector from 'app/App/selectors/failedValidations';
import parentComponentsStackSelector from 'app/Canvas/selectors/parentComponentsStack';
import {
  createDuplicateComponent,
  openParentComponentForEditing
} from 'app/CanvasComponent/actions';

import { COUNTRY_CODE_LOCATION } from 'app/ContentContext/const';
import { notifyAlert, notifyFailure, notifySuccess } from 'app/Notifications/actions';
import { visitComponent } from 'app/Router/actions';
import { savePageFailure } from 'app/Page/actions';
import pageComponentSelector from 'app/Page/selectors/pageComponent';
import pagePayloadSelector from 'app/Page/selectors/pagePayload';
import cwdSiteRootSelector from 'app/Tree/selectors/cwdSiteRoot';
import lanIdSelector from 'app/UserInfo/selectors/lanId';
import { getMostRecentComponentsThunk } from 'app/UserInfo/thunks';

import {
  findLinkURLsToValidate,
  getInvalidLinkMessage,
  identifyInvalidLinks,
  setFailedValidationMessageForField
} from 'lib/validations';
import {
  UI_EDIT_COMPONENT_ON_PAGE,
  duplicateComponent,
  traversePageToFindComponent
} from 'utils/factory';

import {
  saveComponent,
  saveComponentFailure,
  saveComponentSuccess,
  getDynamicValues,
  getDynamicValuesFailure,
  getDynamicValuesSuccess,
  unpublishComponentFailure,
  unpublishComponentRequest,
  unpublishComponentSuccess
} from './actions';

/**
 * get contract dynamic allowed values
 * @param {object} fieldContract - contract for the field needing dynamic values
 * @returns {Function} thunk
 */
export const getDynamicValuesThunk = fieldContract => async (dispatch, _, { betterFetch }) => {
  dispatch(notifyAlert('loading dynamic values...'));
  dispatch(getDynamicValues(fieldContract));
  const {
    allowedValues: { apiUrl, displayName, headers }
  } = fieldContract;

  try {
    const response = await betterFetch(apiUrl, { headers });

    // potentially apply some static transformations here
    let allowedValues = response;

    // pull displayName
    if (displayName) {
      allowedValues = allowedValues.map(value => ({ value, displayName: value[displayName] }));
    }

    dispatch(getDynamicValuesSuccess(fieldContract, allowedValues));
    dispatch(notifySuccess('dynamic values loaded!'));
  } catch (err) {
    dispatch(getDynamicValuesFailure(err));
    dispatch(notifyFailure(err.message));
  }
};

/**
 * thunk to unpublish a dynamic component
 * @param {object} component - the component object to be unpublished
 * @returns {Function} thunk
 */
export const unpublishComponentThunk = component => async (dispatch, getState, { api }) => {
  dispatch(notifyAlert(`Unpublishing component ${component.ComponentName}`));
  try {
    dispatch(unpublishComponentRequest(component));
    const componentData = await api.component.unpublishComponent(component);
    dispatch(unpublishComponentSuccess(component, componentData));
    dispatch(notifySuccess(`Component ${component.ComponentName} is unpublished `));
    dispatch(visitComponent(componentData.Id, componentData.Version));
  } catch (error) {
    dispatch(unpublishComponentFailure(component, error));
    dispatch(notifyFailure(error.message));
  }
};

/**
 * thunk to create a duplicate component from component
 * @returns {undefined}
 */
export const copyComponentThunk = () => (dispatch, getState) => {
  try {
    const component = pageComponentSelector(getState());
    if (isEmpty(component)) {
      throw new Error('No component is found to copy.');
    }
    dispatch(notifyAlert('Creating a duplicate Component'));
    const state = getState();
    const lanId = lanIdSelector(state);
    const newComponent = duplicateComponent({
      component,
      lanId,
      isMaster: true
    });
    dispatch(createDuplicateComponent(newComponent));
    dispatch(
      notifySuccess(
        `A duplicate component has been created as '${newComponent.ComponentName}'`
      )
    );
  } catch (error) {
    dispatch(notifyFailure(error));
  }
};

/**
 * thunk to save a component
 * @param {object} options - object containing force=true/false
 * @returns {object} returns object containing id, version of the component after
 * save or undefined(in case of save failure)
 */
export const saveComponentThunk = (options = {}) => async (dispatch, getState, { api }) => {
  const state = getState();
  const component = pageComponentSelector(state);
  const countryList = get(component, `Payload.MetadataFields.${COUNTRY_CODE_LOCATION}`);
  const site = cwdSiteRootSelector(state);

  try {
    if (isEmpty(component)) {
      throw new Error('No component is found to save.');
    }
    dispatch(notifyAlert(`Saving Component: ${component.ComponentName}`));
    const linksToValidate = findLinkURLsToValidate({ component, countryList, site });
    const editStatus = editStatusSelector(getState());
    dispatch(saveComponent(editStatus, component));

    const response =
      editStatus !== EDIT_STATUS_NEW
        ? await api.component.updateComponent(component, options.force)
        : await api.component.addComponent(component);

    const validatedLinks = await api.misc.validateLinkURLs(linksToValidate);
    const invalidLinks = identifyInvalidLinks(validatedLinks);
    const failureMessage = invalidLinks.length && getInvalidLinkMessage(invalidLinks);
    if (invalidLinks.length > 0) {
      dispatch(notifyFailure(failureMessage));
    }
    dispatch(saveComponentSuccess(component, response));
    dispatch(getMostRecentComponentsThunk());
    dispatch(notifySuccess(`Component Saved: ${component.ComponentName}`));
    return { id: response.Id, version: response.Version };
  } catch (error) {
    if (error.response && error.response.status === 409) {
      dispatch(saveComponentFailure(component, error));
      dispatch(notifyFailure(undefined));
    } else {
      dispatch(notifyFailure(error.message));
    }
    return undefined;
  }
};

/**
 * thunk to save a component
 * @param {string} id - id of the component.
 * @param {string} componentName - name of the component to check the availability.
 * @returns {object} returns object containing id, version of the component after
 * save or undefined(in case of save failure)
 */
export const checkComponentNameAvailabilityThunk = (id, componentName) => async (
  dispatch,
  getState,
  { api }
) => {
  const failedValidations = failedValidationsSelector(getState());
  try {
    await api.component.checkComponentNameAvailability(id, componentName);
  } catch (error) {
    if (error.response && error.response.status === 409) {
      if (get(error, 'body.duplicateNamedComponents')) {
        const newFailedValidations = setFailedValidationMessageForField(
          id,
          'ComponentName',
          `Component name '${componentName}' is not available.`,
          failedValidations
        );
        dispatch(setFailedValidations(newFailedValidations));
        dispatch(saveComponentFailure({}, error));
        dispatch(savePageFailure({}, error));
        dispatch(notifyFailure(undefined));
      }
    }
  }
};

/**
 * thunk to save a component and then visit to the new version
 * @param {object} options - object containing force=true/false
 * @returns {object} returns object containing id, version of the component after
 * save or undefined(in case of save failure)
 */
export const saveAndVisitComponentThunk = (options = {}) => async dispatch => {
  const response = await dispatch(saveComponentThunk(options));
  if (response) {
    dispatch(visitComponent(response.id, response.version));
  }
  return response;
};

/**
 * opens editor for parent component
 * @returns {undefined}
 */
export const openParentComponentForEditingThunk = () => async (dispatch, getState) => {
  const state = getState();
  const pagePayload = pagePayloadSelector(state);
  const stack = parentComponentsStackSelector(state);
  const component = traversePageToFindComponent(pagePayload, stack);
  if (!component) {
    throw new Error('No component is not found.');
  }
  component.uiAction = UI_EDIT_COMPONENT_ON_PAGE;
  dispatch(openParentComponentForEditing(component));
};
